import { useCallback, useState } from "react";
import { ApiError } from "./api";
import { LOCAL, localFiles, newId } from "./temp";
import type { Part } from "./types";

export interface Attachment {
  id: string;
  name: string;
  mime: string;
  size: number;
  meta: Record<string, unknown>;
  chat_id: string | null;
  created_at: string;
  has_text: boolean;
}

export function fileUrl(id: string, download = false) {
  return `/api/files/${id}${download ? "?download=true" : ""}`;
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function uploadFile(file: File, onProgress?: (fraction: number) => void): Promise<Attachment> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/files");
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText));
      else {
        let msg = xhr.statusText;
        try {
          msg = JSON.parse(xhr.responseText).detail ?? msg;
        } catch {
          /* not JSON */
        }
        reject(new ApiError(xhr.status, String(msg)));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, "Upload failed"));
    const form = new FormData();
    form.append("file", file);
    xhr.send(form);
  });
}

export interface PendingFile {
  key: string;
  file: File;
  progress: number;
  preview?: string;
  attachment?: Attachment;
  error?: string;
}

/** Keeps a file in this browser only (private temporary chats). */
async function keepLocally(file: File): Promise<Attachment> {
  const id = LOCAL + newId();
  const mime = file.type || "application/octet-stream";
  await localFiles.put({ id, name: file.name, mime, blob: file });
  return { id, name: file.name, mime, size: file.size, meta: {}, chat_id: null, created_at: new Date().toISOString(), has_text: false };
}

/**
 * Files picked for the next message: uploaded right away, sent as attachment parts. With
 * `local` (private temporary chats) they are kept in this browser instead of being uploaded.
 */
export function usePendingFiles(local = false) {
  const [files, setFiles] = useState<PendingFile[]>([]);

  const add = useCallback((list: FileList | File[]) => {
    for (const file of Array.from(list)) {
      const key = newId();
      const heic = /\.(heic|heif)$/i.test(file.name) || /hei[cf]/i.test(file.type);
      const preview = file.type.startsWith("image/") && !heic ? URL.createObjectURL(file) : undefined;
      setFiles((f) => [...f, { key, file, progress: 0, preview }]);
      if (local) {
        keepLocally(file)
          .then((attachment) => setFiles((f) => f.map((x) => (x.key === key ? { ...x, attachment, progress: 1 } : x))))
          .catch((e: Error) => setFiles((f) => f.map((x) => (x.key === key ? { ...x, error: e.message } : x))));
        continue;
      }
      uploadFile(file, (p) => setFiles((f) => f.map((x) => (x.key === key ? { ...x, progress: p } : x))))
        .then((attachment) =>
          setFiles((f) =>
            f.map((x) => {
              if (x.key !== key) return x;
              // Show the stored image (e.g. an iPhone HEIC photo, converted to JPEG on the server,
              // which the browser could not preview from the original file).
              if (attachment.mime.startsWith("image/") && x.preview) URL.revokeObjectURL(x.preview);
              const preview = attachment.mime.startsWith("image/") ? fileUrl(attachment.id) : x.preview;
              return { ...x, attachment, preview, progress: 1 };
            }),
          ),
        )
        .catch((e: Error) => setFiles((f) => f.map((x) => (x.key === key ? { ...x, error: e.message } : x))));
    }
  }, [local]);

  const remove = useCallback((key: string) => {
    setFiles((f) => {
      const item = f.find((x) => x.key === key);
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return f.filter((x) => x.key !== key);
    });
  }, []);

  const clear = useCallback(() => setFiles([]), []);
  const uploading = files.some((f) => !f.attachment && !f.error);
  const parts: Part[] = files
    .filter((f) => f.attachment)
    .map((f) => ({ type: "attachment", id: f.attachment!.id, name: f.attachment!.name, mime: f.attachment!.mime }));

  return { files, add, remove, clear, uploading, parts };
}
